import React, { FC, useCallback, useEffect } from "react";
import {
  ContentWrapper,
  Heading,
} from "containers/RightPanel/RightPanel.styles";
import { ButtonColor, ButtonType, ButtonVariant } from "enums/Button";
import { useFormik } from "formik";
import { createFieldBlurHandler } from "utils/formValidation";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import styled from "styled-components";
import {
  ButtonsWrapper,
  Content,
  FormWrapper,
} from "../../../../components/Addresses/components/AddressForm.styles";
import Button from "../../../../components/Button/Button";
import DatePicker from "../../../../components/DatePicker/DatePicker";
import Input from "../../../../components/Input/Input";
import { createPassport } from "../../../../reduxFolder/reducers/passports";
import { closeRightPanel } from "../../../../reduxFolder/reducers/rightPanel";
import { FieldWrapper } from "../../../../styles/formStyles";
import { passportFormHelper } from "../../helpers/passportFormHelper";

const FormContent = styled(Content)`
  label,
  & > div {
    position: relative;
    z-index: 0;

    &:hover,
    &:focus-within {
      z-index: 1;
    }
  }
`;

interface PassportFormPanelProps {
  panelData: any;
  isOpen: boolean;
  closeRightPanel: () => void;
  createPassport: (data: any) => Promise<any>;
}

const PassportFormPanel: FC<PassportFormPanelProps> = ({
  isOpen,
  closeRightPanel,
  createPassport,
}) => {
  const { t } = useTranslation("common");
  const formProps = useFormik(
    passportFormHelper(t, createPassport, closeRightPanel),
  );
  const {
    values,
    errors,
    touched,
    handleChange,
    handleSubmit,
    handleReset,
    isSubmitting,
    setFieldValue,
  } = formProps;
  const handleBlur = createFieldBlurHandler(formProps);

  useEffect(() => {
    if (!isOpen) handleReset(null);
  }, [isOpen]);

  return (
    <ContentWrapper>
      <Heading>{t("books.passports.addPassport")}</Heading>
      <FormWrapper onSubmit={handleSubmit}>
        <FormContent>
          <Input
            placeholder={t("books.passports.fullName")}
            name="full_name"
            value={values.full_name}
            onChange={handleChange}
            onBlur={handleBlur}
            tooltip={touched.full_name ? (errors.full_name as string) : ""}
            invalid={touched.full_name && !!errors.full_name}
          />
          <DatePicker
            selected={values.date_of_birth}
            onChange={(date: Date) => setFieldValue("date_of_birth", date)}
            dateFormat="dd.MM.yyyy"
            placeholderText={`${t("books.passports.dateOfBirth")} DD.MM.YYYY`}
            tooltip={
              touched.date_of_birth ? (errors.date_of_birth as string) : ""
            }
            invalid={touched.date_of_birth && !!errors.date_of_birth}
          />
          <FieldWrapper>
            <Input
              placeholder={t("books.passports.serial")}
              name="serial"
              value={values.serial}
              onChange={handleChange}
              onBlur={handleBlur}
              tooltip={touched.serial ? (errors.serial as string) : ""}
              invalid={touched.serial && !!errors.serial}
            />
            <Input
              placeholder={t("books.passports.number")}
              name="number"
              value={values.number}
              onChange={handleChange}
              onBlur={handleBlur}
              tooltip={touched.number ? (errors.number as string) : ""}
              invalid={touched.number && !!errors.number}
            />
          </FieldWrapper>
          <Input
            placeholder={t("books.passports.issuedBy")}
            name="issued_by"
            value={values.issued_by}
            onChange={handleChange}
            onBlur={handleBlur}
            tooltip={touched.issued_by ? (errors.issued_by as string) : ""}
            invalid={touched.issued_by && !!errors.issued_by}
          />
          <Input
            placeholder={t("books.passports.registration")}
            name="registration"
            value={values.registration}
            onChange={handleChange}
            onBlur={handleBlur}
            tooltip={
              touched.registration ? (errors.registration as string) : ""
            }
            invalid={touched.registration && !!errors.registration}
          />
          <Input
            placeholder={t("books.passports.taxId")}
            name="tax_id"
            value={values.tax_id}
            onChange={handleChange}
            onBlur={handleBlur}
            tooltip={touched.tax_id ? (errors.tax_id as string) : ""}
            invalid={touched.tax_id && !!errors.tax_id}
          />
          <DatePicker
            selected={values.issued_date}
            onChange={(date: Date) => setFieldValue("issued_date", date)}
            dateFormat="dd.MM.yyyy"
            placeholderText={`${t("books.passports.issuedDate")} DD.MM.YYYY`}
            tooltip={touched.issued_date ? (errors.issued_date as string) : ""}
            invalid={touched.issued_date && !!errors.issued_date}
          />
        </FormContent>
        <ButtonsWrapper>
          <Button onClick={closeRightPanel}>{t("common.cancel")}</Button>
          <Button
            isLoading={isSubmitting}
            disabled={isSubmitting}
            type={ButtonType.Submit}
            color={ButtonColor.Primary}
            variant={ButtonVariant.Filled}
          >
            {t("books.passports.create")}
          </Button>
        </ButtonsWrapper>
      </FormWrapper>
    </ContentWrapper>
  );
};

const withConnect = connect(null, {
  createPassport,
  closeRightPanel,
});

export default withConnect(PassportFormPanel);
